import type { ReactNode } from 'react';
import { Coins } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'components/ui/badge';
import { Card, CardContent } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { formatDate } from 'lib/utils';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { formatCredits } from 'features/usage/components/usage-credits';
import type { CreditSummary } from '../types';

interface BalanceCardProps {
  summary: CreditSummary;
  action?: ReactNode;
}

const chipClass = 'rounded-full border border-border/70 bg-background/70 px-2.5 py-0.5';

export const BalanceCard = ({ summary, action }: BalanceCardProps) => {
  const { t } = useTranslation();
  const isEmpty = summary.balanceCredits < 1;

  return (
    <Card className="overflow-hidden border-border/70 bg-gradient-to-br from-accent/60 via-card to-card shadow-none">
      <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-background text-foreground shadow-sm ring-1 ring-border/60">
            <Coins aria-hidden="true" className="h-6 w-6" />
          </span>
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-medium text-muted-foreground">
                {t('ORGANIZATION_CREDITS', 'Organization credits')}
              </p>
              {isEmpty && <Badge variant="destructive">{t('NO_CREDITS_LEFT', 'No credits left')}</Badge>}
            </div>
            <p className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-4xl font-semibold tracking-tight tabular-nums text-foreground">
                {formatCredits(summary.balanceCredits)}
              </span>
              <span className="text-sm text-muted-foreground">{t('CREDITS_UNIT', 'credits')}</span>
              <span className="text-sm text-muted-foreground">
                {t('CREDITS_WORTH', {
                  amount: formatMinor(
                    Math.round((Math.floor(summary.balanceCredits) / summary.creditsPerChf) * 100),
                    summary.currency
                  ),
                  defaultValue: 'worth {{amount}}',
                })}
              </span>
            </p>
            <ul className="flex flex-wrap gap-1.5 pt-1 text-xs text-muted-foreground">
              <li className={chipClass}>{t('CREDITS_NEVER_EXPIRES_SHORT', 'Never expires')}</li>
              <li className={chipClass}>
                {t('CREDITS_SHARED_SHORT', 'Shared by everyone in your organization')}
              </li>
              {summary.lastTopUpAtUtc && (
                <li className={chipClass}>
                  {t('LAST_TOPUP_ON', {
                    date: formatDate(summary.lastTopUpAtUtc),
                    defaultValue: 'Last top-up: {{date}}.',
                  })}
                </li>
              )}
            </ul>
          </div>
        </div>
        {action}
      </CardContent>
    </Card>
  );
};

export const BalanceCardSkeleton = () => (
  <Card className="border-border/70 shadow-none">
    <CardContent className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
      <div className="w-full space-y-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-3 w-full max-w-sm" />
      </div>
    </CardContent>
  </Card>
);
